import { SeekLayout } from '@/components/seek/SeekLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Briefcase, Building2, MapPin, Clock, Loader2, ChevronRight, XCircle, Info, Search } from 'lucide-react';
import { detectTextDirection } from '@/lib/textDirection';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useNavigate } from 'react-router-dom';
import { useSeekApplications } from '@/hooks/useSeekApplications';
import { useSeekApplicationTimeline } from '@/hooks/useSeekApplicationTimeline';
import { formatLocalizedDate, formatRelativeTime } from '@/lib/localizedDate';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const STATUS_CLASSNAMES: Record<string, string> = {
  submitted: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  applied: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  under_review: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  shortlisted: 'bg-seek/10 text-seek dark:bg-seek/20 dark:text-seek',
  interview_invitation: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  interview_in_progress: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  interview_completed: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
  offer_stage: 'bg-seek/15 text-seek dark:bg-seek/25 dark:text-seek',
  hired: 'bg-seek/20 text-seek dark:bg-seek/30 dark:text-seek',
  rejected: 'bg-muted text-muted-foreground',
  withdrawn: 'bg-gray-100 text-gray-600 dark:bg-gray-800/40 dark:text-gray-400',
};

type FilterTab = 'active' | 'closed' | 'all';

const CLOSED_STATUSES = ['rejected', 'withdrawn', 'hired'];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readStringField(value: unknown, field: string): string | undefined {
  if (!isRecord(value)) return undefined;
  const fieldValue = value[field];
  return typeof fieldValue === 'string' ? fieldValue : undefined;
}

export default function SeekApplications() {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? i18n.language;
  const navigate = useNavigate();
  const { applications, isLoading, withdrawApplication } = useSeekApplications();
  const [selectedAppId, setSelectedAppId] = useState<string | null>(null);
  const [withdrawConfirmId, setWithdrawConfirmId] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<FilterTab>('active');
  const [searchQuery, setSearchQuery] = useState('');
  const { events, isLoading: timelineLoading } = useSeekApplicationTimeline(selectedAppId);

  const selectedApp = applications.find((a) => a.id === selectedAppId);

  const getStatusLabel = (status: string) => {
    const key = `seek.applications.status.${status}`;
    const translated = t(key);
    return translated === key ? status.replace(/_/g, ' ') : translated;
  };

  const filterTabs: { key: FilterTab; label: string }[] = [
    { key: 'active', label: t('common.active') },
    { key: 'closed', label: t('common.closed') },
    { key: 'all', label: t('common.all') },
  ];

  const filteredApplications = applications.filter((app) => {
    const status = app.application_status_candidate_view ?? 'submitted';
    if (activeFilter === 'active' && CLOSED_STATUSES.includes(status)) return false;
    if (activeFilter === 'closed' && !CLOSED_STATUSES.includes(status)) return false;

    if (searchQuery.trim()) {
      const title = (readStringField(app.job_snapshot_json, 'title') ?? app.seek_job_catalog?.title ?? '').toLowerCase();
      const org = (readStringField(app.job_snapshot_json, 'org_name') ?? app.seek_job_catalog?.org_name ?? '').toLowerCase();
      const q = searchQuery.trim().toLowerCase();
      if (!title.includes(q) && !org.includes(q)) return false;
    }

    return true;
  });

  const closedCount = applications.filter((app) =>
    CLOSED_STATUSES.includes(app.application_status_candidate_view ?? 'submitted')
  ).length;
  const activeCount = applications.length - closedCount;

  return (
    <SeekLayout>
      <div className="seek-page-container seek-page-stack">
        <Card className="seek-hero-panel p-4 sm:p-5 lg:p-6 space-y-4">
          <div>
            <span className="seek-section-kicker mb-3">
              <Briefcase className="h-3.5 w-3.5" />
              {t('seek.nav.applications')}
            </span>
            <h1 className="seek-page-title font-bold text-foreground font-[var(--font-display)]">{t('seek.applications.title')}</h1>
            <p className="text-sm text-muted-foreground mt-1">{t('seek.applications.subtitle')}</p>
          </div>

          <div className="seek-control-bar flex flex-col sm:flex-row items-stretch sm:items-center gap-2 px-3 py-2">
            <div className="relative flex-1 min-w-0 w-full">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t('seek.applications.searchPlaceholder')}
                className="ps-10 border-0 shadow-none bg-transparent focus-visible:ring-0"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {!isLoading && applications.length > 0 && (
              <div className="flex items-center gap-1 border-s border-border ps-2">
                {filterTabs.map((tab) => {
                  const count = tab.key === 'active' ? activeCount : tab.key === 'closed' ? closedCount : applications.length;
                  return (
                    <button
                      key={tab.key}
                      onClick={() => setActiveFilter(tab.key)}
                      className={`px-3 py-1.5 text-sm font-medium rounded-full transition-colors ${
                        activeFilter === tab.key
                          ? 'bg-seek/10 text-seek shadow-sm'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {tab.label}
                      <span className={`ms-1 text-xs px-1.5 py-0.5 rounded-full ${
                        activeFilter === tab.key
                          ? 'bg-seek/15 text-seek'
                          : 'bg-muted text-muted-foreground'
                      }`}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </Card>

        {isLoading ? (
          <div className="seek-empty-state flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : applications.length === 0 ? (
          <Card className="seek-empty-state">
            <CardContent className="py-12 text-center">
              <Briefcase className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">{t('seek.applications.noApplications')}</p>
              <Button variant="outline" onClick={() => navigate('/seek/jobs')}>{t('seek.applications.browseJobs')}</Button>
            </CardContent>
          </Card>
        ) : filteredApplications.length === 0 ? (
          <Card className="seek-empty-state">
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">
                {activeFilter === 'closed' ? t('seek.applications.noClosed') : t('seek.applications.noActive')}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredApplications.map((app) => {
              const job = app.seek_job_catalog;
              const rawStatus = app.application_status_candidate_view ?? 'submitted';
              const statusClassName = STATUS_CLASSNAMES[rawStatus] ?? '';
              const isRejected = rawStatus === 'rejected';

              const displayTitle = readStringField(app.job_snapshot_json, 'title') ?? job?.title ?? t('seek.applications.unknownJob');
              const displayOrg = readStringField(app.job_snapshot_json, 'org_name') ?? job?.org_name;
              const displayLocation = readStringField(app.job_snapshot_json, 'location_text') ?? job?.location_text;

              return (
                <Card
                  key={app.id}
                  className={`seek-card-interactive cursor-pointer ${isRejected ? 'opacity-75' : ''}`}
                  onClick={() => navigate(`/seek/applications/${app.id}`)}
                >
                  <CardContent className="p-4 sm:p-5 seek-status-rail">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-sm sm:text-base truncate">{displayTitle}</h3>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground mt-1">
                          {displayOrg && (
                            <span className="flex items-center gap-1">
                              <Building2 className="w-3 h-3" /><span dir={detectTextDirection(displayOrg)}>{displayOrg}</span>
                            </span>
                          )}
                          {displayLocation && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />{displayLocation}
                            </span>
                          )}
                          {app.submitted_at && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {t('seek.applications.appliedAgo', { time: formatRelativeTime(app.submitted_at, language) })}
                            </span>
                          )}
                        </div>
                        {isRejected && (
                          <div className="flex items-start gap-1.5 mt-2 text-xs text-muted-foreground bg-muted/50 rounded-md px-2.5 py-2">
                            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                            <span>{t('seek.applications.closedReasonMessage')}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className={`text-xs shrink-0 capitalize ${statusClassName}`}>
                          {getStatusLabel(rawStatus)}
                        </Badge>
                        {!['withdrawn', 'hired', 'rejected'].includes(rawStatus) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs text-destructive hover:text-destructive hover:bg-destructive/10 h-7 px-2"
                            disabled={withdrawApplication.isPending}
                            onClick={(e) => {
                              e.stopPropagation();
                              setWithdrawConfirmId(app.id);
                            }}
                          >
                            <XCircle className="w-3.5 h-3.5 me-1" />
                            {t('seek.applications.withdraw')}
                          </Button>
                        )}
                        <ChevronRight className="w-4 h-4 text-muted-foreground hidden sm:block rtl-flip" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Timeline Dialog */}
      <Dialog open={!!selectedAppId} onOpenChange={(o) => !o && setSelectedAppId(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg">
              {(() => {
                const raw = selectedApp?.application_status_candidate_view;
                const snapshotTitle = raw !== 'withdrawn'
                  ? readStringField(selectedApp?.job_snapshot_json, 'title')
                  : undefined;
                return snapshotTitle ?? selectedApp?.seek_job_catalog?.title ?? t('seek.applications.application');
              })()} — {t('seek.applications.timeline')}
            </DialogTitle>
          </DialogHeader>

          {timelineLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : events.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">{t('seek.applications.noUpdates')}</p>
          ) : (
            <ol className="relative border-s border-border ms-3 space-y-4 py-2">
              {events.map((evt) => {
                const mapped = evt.mapped_candidate_stage ?? evt.raw_stage ?? 'unknown';
                return (
                  <li key={evt.id} className="ms-4">
                    <div className="absolute w-2.5 h-2.5 bg-seek rounded-full mt-1.5" style={{ insetInlineStart: '-5.5px' }} />
                    <p className="text-sm font-medium capitalize">{getStatusLabel(mapped)}</p>
                    <p className="text-xs text-muted-foreground">
                      {evt.occurred_at ? formatLocalizedDate(evt.occurred_at, 'PPp', language) : '—'}
                    </p>
                  </li>
                );
              })}
            </ol>
          )}
        </DialogContent>
      </Dialog>
      {/* Withdraw Confirmation */}
      <AlertDialog open={!!withdrawConfirmId} onOpenChange={(o) => !o && setWithdrawConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('seek.applications.confirmWithdraw')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('seek.applications.confirmWithdrawDesc')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (withdrawConfirmId) withdrawApplication.mutate(withdrawConfirmId);
                setWithdrawConfirmId(null);
              }}
            >
              {t('seek.applications.yesWithdraw')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SeekLayout>
  );
}
